// Fingre og mus: tryk, træk fra en node, panorering, knib og hjul.
// Et tryk er under 10 px og 300 ms. Træk skal starte på en node; tom grund panorerer.

import type { P } from '../render/geo';

export interface GestureTarget {
  /** Kan man trække en ledning fra punktet? Returnerer node-id. */
  dragSource(p: P): string | null;
  tap(p: P, touch: boolean): void;
  hover(p: P | null): void;
  dragStart(from: string, p: P, touch: boolean): void;
  dragMove(from: string, p: P, touch: boolean): void;
  dragEnd(from: string, p: P, cancelled: boolean): void;
  pan(dx: number, dy: number): void;
  panEnd(vx: number, vy: number): void;
  panStart(): void;
  zoom(x: number, y: number, factor: number): void;
  /** Ligger en trussel under fingeren? Så tæller et lidt langsommere og lidt rystet tryk også (bevægelige både). */
  softTap?(p: P): boolean;
}

const TAP_PX = 10;
/**
 * IGN-løkke runde 4: "Station på Studstrupværket gør intet og viser ingen toast". En tommelfinger,
 * der hvilede 0,4 s på værket, var hverken et tryk eller et træk, og intet skete. En stille finger
 * er et tryk i op til 1,4 s (pinghjulet i makkerspil afbryder selv trykket ved 1,5 s på en node).
 */
const TAP_MS = 1400;
/** Tryk på en trussel: den bevæger sig, og fingeren følger efter. */
const SOFT_PX = 22;
const SOFT_MS = 800;

interface Ptr {
  id: number;
  x: number;
  y: number;
  x0: number;
  y0: number;
  t0: number;
  touch: boolean;
}

type Mode = 'idle' | 'pending' | 'drag' | 'pan' | 'pinch';

export class Pointer {
  private ptrs = new Map<number, Ptr>();
  private mode: Mode = 'idle';
  private from: string | null = null;
  private trail: { x: number; y: number; t: number }[] = [];
  private pinchD = 0;
  private pinchC: P = { x: 0, y: 0 };
  /** Trykket startede på en trussel: større tolerance for tryk. */
  private soft = false;
  enabled = true;

  /** En finger (eller musen) er nede på en node eller trækker: intet nyt må poppe op under den. */
  get holding(): boolean {
    return this.ptrs.size > 0 && ((this.mode === 'pending' && this.from !== null) || this.mode === 'drag');
  }

  /** Mindst én finger (eller musen) er nede på kortet. */
  get pressed(): boolean {
    return this.ptrs.size > 0;
  }

  constructor(
    private el: HTMLElement,
    private target: GestureTarget,
  ) {
    el.addEventListener('pointerdown', this.down);
    el.addEventListener('pointermove', this.move);
    el.addEventListener('pointerup', this.up);
    el.addEventListener('pointercancel', this.cancel);
    el.addEventListener('pointerleave', () => {
      if (this.ptrs.size === 0) target.hover(null);
    });
    el.addEventListener('wheel', this.wheel, { passive: false });
    el.addEventListener('contextmenu', (e) => e.preventDefault());
    // Safari: ingen sidezoom med knib.
    for (const ev of ['gesturestart', 'gesturechange', 'gestureend']) document.addEventListener(ev, (e) => e.preventDefault(), { passive: false } as AddEventListenerOptions);
  }

  private local(e: PointerEvent | WheelEvent): P {
    const r = this.el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private down = (e: PointerEvent): void => {
    if (!this.enabled) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    try {
      this.el.setPointerCapture(e.pointerId);
    } catch {
      // Nogle browsere afviser capture for syntetiske hændelser.
    }
    const p = this.local(e);
    const ptr: Ptr = { id: e.pointerId, x: p.x, y: p.y, x0: p.x, y0: p.y, t0: performance.now(), touch: e.pointerType !== 'mouse' };
    this.ptrs.set(e.pointerId, ptr);
    if (this.ptrs.size === 2) {
      // Anden finger: afbryd træk og gå over til knib.
      if (this.mode === 'drag' && this.from) this.target.dragEnd(this.from, p, true);
      this.mode = 'pinch';
      this.from = null;
      const [a, b] = [...this.ptrs.values()] as [Ptr, Ptr];
      this.pinchD = Math.hypot(a.x - b.x, a.y - b.y);
      this.pinchC = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      this.target.panStart();
      return;
    }
    if (this.ptrs.size > 2) return;
    this.mode = 'pending';
    this.from = this.target.dragSource(p);
    this.soft = !this.from && (this.target.softTap?.(p) ?? false);
    this.trail = [{ x: p.x, y: p.y, t: ptr.t0 }];
    this.target.panStart();
  };

  private move = (e: PointerEvent): void => {
    const p = this.local(e);
    const ptr = this.ptrs.get(e.pointerId);
    if (!ptr) {
      if (e.pointerType === 'mouse') this.target.hover(p);
      return;
    }
    e.preventDefault();
    const px = ptr.x;
    const py = ptr.y;
    ptr.x = p.x;
    ptr.y = p.y;
    if (this.mode === 'pinch') {
      const [a, b] = [...this.ptrs.values()] as [Ptr, Ptr];
      if (!a || !b) return;
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      const c = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      this.target.pan(c.x - this.pinchC.x, c.y - this.pinchC.y);
      if (this.pinchD > 0 && d > 0) this.target.zoom(c.x, c.y, d / this.pinchD);
      this.pinchD = d;
      this.pinchC = c;
      return;
    }
    if (this.mode === 'pending') {
      if (Math.hypot(p.x - ptr.x0, p.y - ptr.y0) < (this.soft ? SOFT_PX : TAP_PX)) return;
      if (this.from) {
        this.mode = 'drag';
        this.target.dragStart(this.from, p, ptr.touch);
      } else {
        this.mode = 'pan';
        // Tag de første pixels med, så kortet ikke hopper.
        this.target.pan(p.x - ptr.x0, p.y - ptr.y0);
        return;
      }
    }
    if (this.mode === 'drag' && this.from) {
      this.target.dragMove(this.from, p, ptr.touch);
    } else if (this.mode === 'pan') {
      this.target.pan(p.x - px, p.y - py);
      const now = performance.now();
      this.trail.push({ x: p.x, y: p.y, t: now });
      while (this.trail.length > 2 && now - (this.trail[0]?.t ?? now) > 100) this.trail.shift();
    }
  };

  private up = (e: PointerEvent): void => {
    const ptr = this.ptrs.get(e.pointerId);
    if (!ptr) return;
    e.preventDefault();
    this.ptrs.delete(e.pointerId);
    const p = this.local(e);
    if (this.mode === 'pinch') {
      if (this.ptrs.size === 0) this.mode = 'idle';
      else {
        // Én finger tilbage: fortsæt som panorering.
        this.mode = 'pan';
        this.trail = [];
      }
      return;
    }
    if (this.mode === 'pending') {
      const dt = performance.now() - ptr.t0;
      if (dt < (this.soft ? SOFT_MS : TAP_MS) && Math.hypot(p.x - ptr.x0, p.y - ptr.y0) < (this.soft ? SOFT_PX : TAP_PX)) {
        // En trussel, der sejlede lidt under fingeren, rammes stadig: trykket gælder, hvor fingeren landede.
        this.target.tap(this.soft && !this.target.softTap?.(p) ? { x: ptr.x0, y: ptr.y0 } : p, ptr.touch);
      }
    } else if (this.mode === 'drag' && this.from) {
      this.target.dragEnd(this.from, p, false);
    } else if (this.mode === 'pan') {
      const a = this.trail[0];
      const b = this.trail[this.trail.length - 1];
      if (a && b && b.t - a.t > 10 && performance.now() - b.t < 80) this.target.panEnd((b.x - a.x) / (b.t - a.t), (b.y - a.y) / (b.t - a.t));
      else this.target.panEnd(0, 0);
    }
    this.mode = 'idle';
    this.from = null;
  };

  private cancel = (e: PointerEvent): void => {
    this.ptrs.delete(e.pointerId);
    if (this.mode === 'drag' && this.from) this.target.dragEnd(this.from, this.local(e), true);
    if (this.ptrs.size === 0) {
      this.mode = 'idle';
      this.from = null;
    }
  };

  private wheel = (e: WheelEvent): void => {
    e.preventDefault();
    if (!this.enabled) return;
    const p = this.local(e);
    const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
    this.target.zoom(p.x, p.y, Math.exp(-dy * (e.ctrlKey ? 0.01 : 0.0015)));
  };
}
